import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Avatar,
  TextField,
  IconButton,
  Paper,
  CircularProgress,
  Button,
  Chip,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaPaperclip, FaTimes } from "react-icons/fa";
import { getData, postData, postFileUpload } from "../../Webservice/Webservice";
import { IoSendSharp } from "react-icons/io5";

const THEME_COLOR = "rgb(255, 209, 2)";

const Chat = () => {
  const { queryId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const [queryData, setQueryData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileInputRef, setFileInputRef] = useState(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchQueryDetails = async () => {
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      const response = await getData(`query/view/${queryId}`, null, token);

      if (response && response.success) {
        setQueryData(response.query);
        setMessages(response.messages || []);
      } else {
        setError("Failed to fetch query details");
      }
    } catch (err) {
      setError("An error occurred while fetching query details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (queryId) {
      fetchQueryDetails();
    }
  }, [queryId]);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async () => {
    if (newMessage.trim() || selectedFiles.length > 0) {
      try {
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        
        const formData = new FormData();
        formData.append("queryId", queryId);
        formData.append("message", newMessage.trim());
        selectedFiles.forEach((file) => formData.append("images", file));

        const response = await postFileUpload("query/reply", formData, token);
        if (response && response.success) {
          setNewMessage("");
          setSelectedFiles([]);
          await fetchQueryDetails();
        }
      } catch (error) {
        console.error("Error sending message:", error);
      }
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      const res = await postData(`query/update/${queryId}`, { status: newStatus }, token);
      if (res && res.success) {
        fetchQueryDetails();
      }
    } catch (error) {
       console.error("Error updating status:", error);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            color: "white",
          }}
        >
          <CircularProgress sx={{ color: "white", mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 300 }}>
            Loading conversation...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            color: "white",
            p: 3,
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 500 }}>
            Oops!
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/queries")}
            sx={{
              backgroundColor: "white",
              color: "#ee5a24",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.9)",
              },
            }}
          >
            Back to Support Tickets
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "96vh",
        display: "flex",
        flexDirection: "column",
        padding: "0px 80px",
      }}
    >
      {/* Enhanced Header */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderRadius: 0,
          backgroundColor: "rgb(255, 209, 2)",
          color: "black",
        }}
      >
        <IconButton
          onClick={() => navigate("/queries")}
          sx={{
            color: "black",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.05)",
            },
          }}
        >
          <FaArrowLeft />
        </IconButton>
        
        <Avatar
          sx={{
            bgcolor: "white",
            width: 48,
            height: 48,
            border: "2px solid rgba(0, 0, 0, 0.1)",
            color: "black"
          }}
        >
          {queryData?.customerId?.name?.charAt(0)?.toUpperCase() || "C"}
        </Avatar>
        
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 700,
              fontSize: "1.2rem",
            }}
          >
            {queryData?.subject || "Support Ticket"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.2 }}>
            <Typography
              variant="caption"
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                fontSize: "0.8rem",
              }}
            >
              {queryData?.customerId?.name}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.6 }}>•</Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                fontSize: "0.8rem",
              }}
            >
              Ref: {queryData?.referenceNo}
            </Typography>
            {queryData?.orderId && (
              <>
                <Typography variant="caption" sx={{ opacity: 0.6 }}>•</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, bgcolor: "rgba(0,0,0,0.1)", px: 1, borderRadius: "4px" }}>
                  Order: #{queryData.orderId.orderNumber}
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {/* Status Actions */}
        <Box sx={{ display: "flex", gap: 1 }}>
          {queryData?.status !== "in-progress" && queryData?.status !== "resolved" && (
            <Button
              size="small"
              variant="contained"
              sx={{ bgcolor: "#333", color: "white", textTransform: "none", borderRadius: "20px", "&:hover": { bgcolor: "#000" } }}
              onClick={() => updateStatus("in-progress")}
            >
              Mark In-Progress
            </Button>
          )}
          {queryData?.status !== "resolved" && (
            <Button
              size="small"
              variant="contained"
              sx={{ bgcolor: "#4caf50", color: "white", textTransform: "none", borderRadius: "20px", "&:hover": { bgcolor: "#388e3c" } }}
              onClick={() => updateStatus("resolved")}
            >
              Resolve Ticket
            </Button>
          )}
          {queryData?.status === "resolved" && (
            <Chip label="RESOLVED" sx={{ bgcolor: "white", color: "#4caf50", fontWeight: 700 }} />
          )}
        </Box>
      </Paper>

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          background: "#f0f2f5",
        }}
      >
        {/* Initial Customer Message */}
        <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, maxWidth: "75%" }}>
               <Avatar sx={{ width: 32, height: 32, bgcolor: "rgb(255, 209, 2)", color: "black", fontSize: "0.8rem" }}>C</Avatar>
               <Paper elevation={0} sx={{ p: 2, borderRadius: "18px 18px 18px 4px", bgcolor: "white", border: "1px solid #e1e4e8" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", color: THEME_COLOR, mb: 0.5 }}>ORIGINAL REQUEST</Typography>
                  <Typography sx={{ fontSize: "0.95rem" }}>{queryData?.description}</Typography>
                  {queryData?.images?.length > 0 && (
                    <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {queryData.images.map((img, i) => (
                        <img key={i} src={img} style={{ width: 120, height: 120, objectFit: "cover", borderRadius: "8px", border: "1px solid #eee", cursor: "pointer" }} onClick={() => window.open(img, '_blank')} />
                      ))}
                    </Box>
                  )}
                  <Typography sx={{ fontSize: "0.7rem", opacity: 0.6, mt: 1, textAlign: "right" }}>{new Date(queryData?.createdAt).toLocaleString()}</Typography>
               </Paper>
            </Box>
        </Box>

        {messages.map((message, index) => {
          // Use msg.senderRole if available, otherwise fallback logic
          // Note: our API now includes senderRole in each message.
          const isCustomer = message.senderRole === "Customer";
          
          return (
            <Box
              key={message._id || index}
              sx={{
                display: "flex",
                justifyContent: isCustomer ? "flex-start" : "flex-end",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 1,
                  maxWidth: "75%",
                  flexDirection: isCustomer ? "row" : "row-reverse",
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: isCustomer ? "rgb(255, 209, 2)" : "#333",
                    color: isCustomer ? "black" : "white",
                    fontSize: "0.8rem",
                  }}
                >
                  {isCustomer ? "C" : "A"}
                </Avatar>
                
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: isCustomer 
                      ? "18px 18px 18px 4px" 
                      : "18px 18px 4px 18px",
                    backgroundColor: isCustomer ? "white" : "#333",
                    color: isCustomer ? "black" : "white",
                    border: isCustomer ? "1px solid #e1e4e8" : "none"
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "0.95rem",
                      lineHeight: 1.4,
                      wordBreak: "break-word",
                    }}
                  >
                    {message.message}
                  </Typography>
                  
                  {message.images && message.images.length > 0 && (
                    <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {message.images.map((img, i) => (
                        <img key={i} src={img} style={{ width: 100, height: 100, objectFit: "cover", borderRadius: "8px", cursor: "pointer" }} onClick={() => window.open(img, '_blank')} />
                      ))}
                    </Box>
                  )}
                  
                  <Box sx={{ mt: 1, textAlign: isCustomer ? "left" : "right" }}>
                    <Typography variant="caption" sx={{ fontSize: "0.7rem", opacity: 0.6 }}>
                      {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Box>
          );
        })}
        <div ref={messagesEndRef} />
      </Box>

      {/* Enhanced Message Input */}
      {queryData?.status !== "resolved" ? (
        <Box sx={{ p: 2, backgroundColor: "white", borderTop: "1px solid #eee" }}>
          {selectedFiles.length > 0 && (
            <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {selectedFiles.map((file, index) => (
                <Chip
                    key={index}
                    label={file.name}
                    onDelete={() => removeFile(index)}
                    size="small"
                />
                ))}
            </Box>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton component="label">
              <FaPaperclip size={20} color={THEME_COLOR} />
              <input type="file" hidden multiple onChange={handleFileSelect} />
            </IconButton>
            <TextField
              fullWidth
              placeholder="Write a reply..."
              variant="outlined"
              size="small"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "24px" } }}
            />
            <IconButton 
                disabled={!newMessage.trim() && selectedFiles.length === 0}
                onClick={handleSendMessage}
                sx={{ bgcolor: THEME_COLOR, color: "black", "&:hover": { bgcolor: "#b08a46" }, "&.Mui-disabled": { opacity: 0.5 } }}
            >
              <IoSendSharp size={20} />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 3, textAlign: "center", bgcolor: "#f8f9fa", borderTop: "1px solid #eee" }}>
            <Typography sx={{ fontWeight: 600, color: "#999" }}>Ticket is resolved. No further replies allowed.</Typography>
        </Box>
      )}
    </Box>
  );
};

export default Chat;